import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import { File as FilePdf } from 'lucide-react';

interface BusinessReportPDFProps {
  title: string;
  content: string;
  companyName?: string;
}

/**
 * Component for generating PDF business reports with professional formatting
 */
export const BusinessReportPDF: React.FC<BusinessReportPDFProps> = ({ 
  title, 
  content, 
  companyName = 'Su Empresa' 
}) => {
  const [isGenerating, setIsGenerating] = useState(false);

  // Process the content into structured sections
  const processContent = (text: string): { 
    title: string, 
    sections: Array<{ 
      isHeading: boolean, 
      isList: boolean, 
      content: string,
      level: number
    }> 
  } => {
    if (!text || text.trim() === '') {
      return { 
        title: title || 'Documento Empresarial',
        sections: [{ isHeading: false, isList: false, content: 'No se pudo recuperar texto del documento.', level: 0 }] 
      };
    }
    
    // Split text into lines and filter out empty ones
    const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
    
    // Use provided title or first line as document title
    const documentTitle = title || (lines.length > 0 ? lines[0] : 'Documento Empresarial');
    
    // Process content lines (skip first line if it's being used as title)
    const contentLines = title ? lines : (lines.length > 1 ? lines.slice(1) : []);
    
    // Group lines into paragraphs and identify headings and lists
    const sections: Array<{ isHeading: boolean, isList: boolean, content: string, level: number }> = [];
    let currentParagraph = '';
    
    contentLines.forEach(line => {
      const trimmedLine = line.trim();
      
      // If empty line and we have content, push the current paragraph
      if (trimmedLine === '') {
        if (currentParagraph !== '') {
          sections.push({ 
            isHeading: false, 
            isList: false, 
            content: currentParagraph,
            level: 0
          });
          currentParagraph = '';
        }
        return;
      }
      
      // Check if it's a heading (short line without ending punctuation)
      // Also detect numbered headings like "1. Title" or "Article 1."
      const headingMatch = trimmedLine.match(/^(\d+\.\s*|\*\*|\#\#*\s+|Artículo\s+\d+[\.\:])(.+)$/i);
      const isNumberedHeading = !!headingMatch;
      
      if ((trimmedLine.length < 60 && 
          !trimmedLine.endsWith('.') && 
          !trimmedLine.endsWith('?') && 
          !trimmedLine.endsWith('!')) || isNumberedHeading) {
        
        // If we have content, push it first
        if (currentParagraph !== '') {
          sections.push({ 
            isHeading: false, 
            isList: false, 
            content: currentParagraph,
            level: 0
          });
          currentParagraph = '';
        }
        
        // Determine heading level
        let level = 1;
        let headingText = trimmedLine;
        
        if (isNumberedHeading) {
          // For numbered headings, use the captured content
          headingText = headingMatch[2].trim();
          
          // Check if it's a major heading (Article, Section, etc.)
          if (headingMatch[1].toLowerCase().includes('artículo') || 
              headingMatch[1].toLowerCase().includes('article')) {
            level = 1;
          } else {
            level = 2;
          }
        } else {
          // For non-numbered headings, check for patterns
          if (trimmedLine.startsWith('##')) {
            level = 2;
            headingText = trimmedLine.replace(/^##\s*/, '');
          } else if (trimmedLine.startsWith('#')) {
            level = 1;
            headingText = trimmedLine.replace(/^#\s*/, '');
          } else if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
            level = 2;
            headingText = trimmedLine.replace(/^\*\*|\*\*$/g, '');
          }
        }
        
        // Add the heading
        sections.push({ 
          isHeading: true, 
          isList: false, 
          content: headingText,
          level
        });
        return;
      }
      
      // Check if it's a list item
      const listMatch = trimmedLine.match(/^([\d\-\*\•]+[\.\)\-\s])\s*(.+)$/);
      if (listMatch) {
        // If we have content, push it first
        if (currentParagraph !== '') {
          sections.push({ 
            isHeading: false, 
            isList: false, 
            content: currentParagraph,
            level: 0
          });
          currentParagraph = '';
        }
        
        // Add the list item
        sections.push({ 
          isHeading: false, 
          isList: true, 
          content: listMatch[2].trim(),
          level: listMatch[1].trim().length // Use bullet length as indentation level
        });
        return;
      }
      
      // Regular paragraph content
      if (currentParagraph !== '') {
        currentParagraph += ' ';
      }
      currentParagraph += trimmedLine;
    });
    
    // Add any remaining paragraph
    if (currentParagraph !== '') {
      sections.push({ 
        isHeading: false, 
        isList: false, 
        content: currentParagraph,
        level: 0
      });
    }
    
    return { title: documentTitle, sections };
  };

  // Generate and download the PDF
  const generatePDF = async () => {
    try {
      setIsGenerating(true);
      
      // Create new PDF document (A4 format)
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Set document properties
      doc.setProperties({
        title: title || 'Documento Empresarial',
        subject: 'Informe Empresarial',
        author: companyName,
        creator: 'Business Report Generator'
      });
      
      // Process content
      const { title: docTitle, sections } = processContent(content);
      
      // Set up document styles
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      
      // Add current date at the top right
      const currentDate = new Date().toLocaleDateString('es-ES', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(9);
      doc.text(currentDate, 190, 15, { align: 'right' });
      
      // Add company logo or name at the top left
      doc.setFontSize(12);
      doc.setTextColor(44, 85, 123); // Blue color
      doc.text(companyName, 20, 15);
      
      // Add horizontal line
      doc.setDrawColor(200, 200, 200);
      doc.line(20, 20, 190, 20);
      
      // Add title
      doc.setFontSize(18);
      doc.setTextColor(44, 85, 123); // Blue color
      doc.text(docTitle, 105, 35, { align: 'center' });
      
      // Add content
      let yPosition = 50;
      const pageWidth = 210; // A4 width in mm
      const margin = 20;
      const textWidth = pageWidth - (2 * margin);
      
      // Process each section
      sections.forEach(section => {
        // Check if we need a new page
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 30;
        }
        
        if (section.isHeading) {
          // Add heading with proper formatting based on level
          if (section.level === 1) {
            // Major heading (like Article)
            doc.setFontSize(14);
            doc.setTextColor(44, 85, 123); // Blue color
            doc.setFont('helvetica', 'bold');
            doc.text(section.content, 20, yPosition);
            yPosition += 8;
          } else {
            // Subheading
            doc.setFontSize(12);
            doc.setTextColor(60, 60, 60);
            doc.setFont('helvetica', 'bold');
            doc.text(section.content, 20, yPosition);
            yPosition += 6;
          }
        } else if (section.isList) {
          // Add list item with proper indentation
          doc.setFontSize(11);
          doc.setTextColor(60, 60, 60);
          doc.setFont('helvetica', 'normal');
          
          // Calculate indentation based on level
          const indent = 20 + (section.level * 2);
          
          // Add bullet point
          doc.text('•', indent, yPosition);
          
          // Split text to fit within margins with proper indentation
          const textLines = doc.splitTextToSize(section.content, textWidth - (indent - 15));
          doc.text(textLines, indent + 5, yPosition);
          
          // Update position based on number of lines
          yPosition += 5 * textLines.length;
        } else {
          // Regular paragraph
          doc.setFontSize(11);
          doc.setTextColor(60, 60, 60);
          doc.setFont('helvetica', 'normal');
          
          // Split text to fit within margins
          const textLines = doc.splitTextToSize(section.content, textWidth);
          doc.text(textLines, 20, yPosition);
          
          // Update position based on number of lines
          yPosition += 5 * textLines.length + 3;
        }
      });
      
      // Add footer
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        
        // Add horizontal line
        const footerY = 285;
        doc.setDrawColor(200, 200, 200);
        doc.line(20, footerY, 190, footerY);
        
        // Add footer text
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Documento generado automáticamente | © ${new Date().getFullYear()} ${companyName}`, 105, footerY + 5, { align: 'center' });
        
        // Add page number
        doc.text(`Página ${i} de ${pageCount}`, 190, footerY + 5, { align: 'right' });
      }
      
      // Save the PDF
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      doc.save(`informe-${timestamp}.pdf`);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error al generar el informe PDF');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold mb-3 text-purple-700">Informe PDF Empresarial</h3>
      <p className="text-sm text-gray-600 mb-4">
        Genera un documento PDF profesional con formato empresarial, ideal para presentaciones
        formales, informes y documentación oficial.
      </p>
      
      <div className="flex flex-col space-y-3">
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-700 w-24">Título:</span>
          <span className="text-sm text-gray-600">{title || 'Documento Empresarial'}</span>
        </div>
        
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-700 w-24">Empresa:</span>
          <span className="text-sm text-gray-600">{companyName}</span>
        </div>
        
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-700 w-24">Contenido:</span>
          <span className="text-sm text-gray-600 truncate">{content.substring(0, 50)}...</span>
        </div>
      </div>
      
      <button
        onClick={generatePDF}
        disabled={isGenerating}
        className="mt-4 w-full px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors flex items-center justify-center disabled:opacity-70"
      >
        {isGenerating ? (
          'Generando PDF...'
        ) : (
          <>
            <FilePdf className="w-5 h-5 mr-2" />
            Descargar Informe PDF
          </>
        )}
      </button>
    </div>
  );
};